import { Capacitor, registerPlugin } from '@capacitor/core';

interface SharedDataPlugin {
  setData(options: { events?: string; tasks?: string }): Promise<{ success: boolean }>;
  getData(): Promise<{ events: string; tasks: string; lastUpdated: number }>;
  setApiKey(options: { apiKey: string; baseUrl: string }): Promise<{ success: boolean }>;
  getApiKey(): Promise<{ apiKey: string; baseUrl: string }>;
}

// Register the native plugin — only works on iOS with the native SharedDataPlugin
const SharedData = registerPlugin<SharedDataPlugin>('SharedData');

/**
 * Push calendar events and tasks data to native iOS widget storage.
 * Only works on iOS native platform. No-op on web.
 */
export const pushWidgetData = async (
  events: Array<{ id: string; title: string; start_time: string; end_time: string; is_all_day: boolean; calendar_color?: string | null }>,
  tasks: Array<{ id: string; title: string; priority: number | string | null | undefined; due_date?: string | null; status: string }>
): Promise<void> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return;
  }

  try {
    // Serialize to JSON for native storage
    const eventsJson = JSON.stringify(events.map(e => ({
      id: e.id,
      title: e.title,
      start_time: e.start_time,
      end_time: e.end_time,
      is_all_day: e.is_all_day,
      calendar_color: e.calendar_color || null,
    })));

    const tasksJson = JSON.stringify(tasks.map(t => ({
      id: t.id,
      title: t.title,
      priority: typeof t.priority === 'number' ? t.priority : null,
      due_date: t.due_date || null,
      status: t.status,
    })));

    await SharedData.setData({ events: eventsJson, tasks: tasksJson });
  } catch (error) {
    // Widget bridge not available — silently ignore (web platform or plugin not registered)
    console.warn('[WidgetBridge] Failed to push widget data:', error);
  }
};

export const getWidgetData = async (): Promise<{ events: string; tasks: string; lastUpdated: number } | null> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return null;
  }

  try {
    return await SharedData.getData();
  } catch (error) {
    console.warn('[WidgetBridge] Failed to get widget data:', error);
    return null;
  }
};

/**
 * Push API key and base URL to native iOS shared storage for the share extension.
 * Only works on iOS native platform. No-op on web.
 */
export const pushShareApiKey = async (apiKey: string, baseUrl: string): Promise<void> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return;
  }

  try {
    await SharedData.setApiKey({ apiKey, baseUrl });
  } catch (error) {
    console.warn('[WidgetBridge] Failed to push share API key:', error);
  }
};

/**
 * Get API key and base URL from native iOS shared storage.
 * Only works on iOS native platform. Returns null on web.
 */
export const getShareApiKey = async (): Promise<{ apiKey: string; baseUrl: string } | null> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return null;
  }

  try {
    return await SharedData.getApiKey();
  } catch (error) {
    console.warn('[WidgetBridge] Failed to get share API key:', error);
    return null;
  }
};

